import { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import HeaderBar from "../../../components/Header/HeaderBar";
import { CurrentUser } from "../../../store/storeTypes";
import axios from "../../../services/axios/backendAxios";
import flash from "../../../services/flash";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { invalidateQueriesContaining } from "../../../utils/queryInvalidation";
import {
  JobServiceOption,
  JobBulkUpdatePayload,
  SeasonDetails,
  Job,
} from "@mysnowscape/snow-lib";
import { Company, Season, SelectiveJobBulkUpdatePayload } from "../../../types";
import errors from "../../../services/errors";
import ServicesField from "../../../components/Services/ServicesField";
import CheckBox from "../../../components/Inputs/CheckBox";
import LoadingIcon from "../../../components/Loaders/LoadingIcon";
import EmailField from "../../../components/Email/EmailField";
import ReportSettingsField from "../../../components/Report/ReportSettingsField";
import { RequiredImageSelector } from "./EditJobPage";
import ContentWrapper from "../../../components/ContentWrapper";

function BulkEditModal({ currentUser }: { currentUser: CurrentUser }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const season: Season = (params.get("season") ?? Season.snow) as Season;
  const jobIds: string[] = params.get("jobIds")?.split(",") ?? [];
  if (jobIds.length < 1) {
    throw new Error("Must supply at least 1 job id");
  }
  const [data, setData] = useState<SeasonDetails | null>(null);
  const [servicesToAdd, setServicesToAdd] = useState<JobServiceOption[]>([]);
  const [servicesToRemove, setServicesToRemove] = useState<JobServiceOption[]>(
    [],
  );
  const [editServices, setEditServices] = useState<boolean>(false);
  const [fieldsToEdit, setFieldsToEdit] = useState<
    Partial<Record<keyof JobBulkUpdatePayload["data"], boolean>>
  >({});
  const queryClient = useQueryClient();
  const companyQuery = useQuery<Company>({
    queryKey: [`company/${currentUser.currentCompanyId}`],
    enabled: currentUser.currentCompanyId != null,
  });
  const jobQuery = useQuery<Job>({
    queryKey: [`job/${jobIds[0]}`],
    enabled: currentUser?.currentCompanyId != null,
  });
  useEffect(() => {
    if (companyQuery.data && jobQuery.data && data === null) {
      const myData: SeasonDetails = jobQuery.data[season]!;
      setData(myData);
    }
  }, [companyQuery.data, jobQuery.data]);

  useEffect(() => {
    if (companyQuery.data) {
      const common = companyQuery.data.commonCompanyServices?.[season] || [];
      setServicesToAdd(common.map((s) => ({ ...s, checked: false })));
      setServicesToRemove(common.map((s) => ({ ...s, checked: false })));
    }
  }, [companyQuery.data, season]);

  const handleSetServicesToAdd = (updatedList: JobServiceOption[]) => {
    setServicesToAdd(updatedList);
    const checkedKeys = new Set(
      updatedList.filter((s) => s.checked).map((s) => s.key),
    );
    // Remove checked jobs from servicesToRemove to prevent adding and removing the same service
    setServicesToRemove((prev) =>
      prev.map((s) => (checkedKeys.has(s.key) ? { ...s, checked: false } : s)),
    );
  };

  const handleSetServicesToRemove = (updatedList: JobServiceOption[]) => {
    setServicesToRemove(updatedList);
    const checkedKeys = new Set(
      updatedList.filter((s) => s.checked).map((s) => s.key),
    );
    // Again, remove checked jobs from servicesToAdd to prevent adding and removing the same service
    setServicesToAdd((prev) =>
      prev.map((s) => (checkedKeys.has(s.key) ? { ...s, checked: false } : s)),
    );
  };

  const hasServicesToUpdate =
    editServices &&
    (servicesToAdd.some((s) => s.checked) ||
      servicesToRemove.some((s) => s.checked));
  const hasOtherFieldsToUpdate = Object.values(fieldsToEdit).some(Boolean);
  const isUpdateDisabled = !hasServicesToUpdate && !hasOtherFieldsToUpdate;

  const sectionClass = "mb-6 border border-gray-300 p-4";
  return (
    <div className="flex flex-col items-center">
      <HeaderBar
        title={"Bulk Edit"}
        buttons={[
          {
            color: "green",
            label: `Update ${jobIds.length} Properties`,
            disabled: isUpdateDisabled,
            onClick: async () => {
              try {
                const payload: SelectiveJobBulkUpdatePayload = {
                  season,
                  jobIds,
                  data: {},
                };
                if (hasServicesToUpdate) {
                  const toAdd = servicesToAdd
                    .filter((s) => s.checked)
                    .map((s) => s.key || s.label);
                  const toRemove = servicesToRemove
                    .filter((s) => s.checked)
                    .map((s) => s.key || s.label);
                  if (toAdd.length > 0) {
                    payload.data.servicesToAdd = toAdd;
                  }
                  if (toRemove.length > 0) {
                    payload.data.servicesToRemove = toRemove;
                  }
                }
                for (const key in fieldsToEdit) {
                  // @ts-expect-error: key is the correct.
                  if (fieldsToEdit[key]) {
                    // @ts-expect-error: key is the correct.
                    payload.data[key] = data[key];
                  }
                }
                if (Object.keys(payload.data).length < 1) {
                  alert("Please select at least 1 field to edit");
                  return;
                }
                const r = await axios.post("/job/bulk-update", payload);
                flash.success(`Updated ${r.data.modifiedCount} properties`);
                queryClient.invalidateQueries({
                  queryKey: ["jobs"],
                  refetchType: "all",
                });
                invalidateQueriesContaining(queryClient, ["job"], {
                  refetchType: "all",
                });
                navigate("/admin/property");
              } catch (err) {
                errors.report(err);
                flash.error(`Failed to update: ${err}`);
              }
            },
          },
        ]}
      />
      <ContentWrapper loading={false} className="">
        {data === null ? (
          <LoadingIcon />
        ) : (
          <form>
            <p className="text-gray-700 mb-6">
              You are editing {jobIds.length} properties. Changes will affect
              the {season} season only. Values shown below are based on the
              first property selected.
            </p>
            {companyQuery.isPending ? (
              <LoadingIcon />
            ) : (
              <>
                <section className={sectionClass}>
                  <CheckBox
                    label="Edit Services"
                    checked={editServices}
                    onChange={setEditServices}
                  />
                  <div
                    style={{
                      paddingLeft: 30,
                      opacity: editServices ? 1 : 0.3,
                    }}
                    className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-3"
                  >
                    <div>
                      <ServicesField
                        enableAddNew={false}
                        enableSelectAll={true}
                        label="Services To Add"
                        services={servicesToAdd}
                        season={season}
                        setServices={handleSetServicesToAdd}
                        subtitle={`Select ${season} services to add to all selected properties`}
                        readOnly={!editServices}
                      />
                    </div>
                    <div>
                      <ServicesField
                        enableAddNew={false}
                        enableSelectAll={true}
                        label="Services To Remove"
                        services={servicesToRemove}
                        season={season}
                        setServices={handleSetServicesToRemove}
                        subtitle={`Select ${season} services to remove from all selected properties`}
                        readOnly={!editServices}
                      />
                    </div>
                  </div>
                </section>

                <section className={sectionClass}>
                  <CheckBox
                    label="Edit Season Enabled"
                    onChange={(checked) =>
                      setFieldsToEdit((prev) => ({ ...prev, enabled: checked }))
                    }
                  />
                  <div
                    style={{
                      paddingLeft: 30,
                      opacity: fieldsToEdit.enabled === true ? 1 : 0.3,
                    }}
                  >
                    <CheckBox
                      label={`Enable ${season} Season`}
                      checked={data.enabled}
                      onChange={(checked) =>
                        setData((prev) => ({
                          ...prev,
                          enabled: checked,
                        }))
                      }
                      disabled={fieldsToEdit.enabled !== true}
                    />
                  </div>
                </section>

                <section className={sectionClass}>
                  <CheckBox
                    label="Edit Email Report"
                    onChange={(checked) =>
                      setFieldsToEdit((prev) => ({
                        ...prev,
                        report: checked,
                        requireReportEmails: checked,
                      }))
                    }
                  />
                  <div
                    style={{
                      paddingLeft: 30,
                      opacity: fieldsToEdit.report === true ? 1 : 0.3,
                    }}
                  >
                    <CheckBox
                      checked={data.requireReportEmails ?? true}
                      onChange={(checked) =>
                        setData((prev) => ({
                          ...prev!,
                          requireReportEmails: checked,
                        }))
                      }
                      label="Enable Email Reports"
                      disabled={fieldsToEdit.requireReportEmails !== true}
                    />
                    <EmailField
                      enableAdd={fieldsToEdit.report === true}
                      enableDelete={fieldsToEdit.report === true}
                      enableEdit={fieldsToEdit.report === true}
                      emailList={data.report?.emailList || []}
                      setEmailList={(updatedList: []) => {
                        const newReportValue = data.report ?? {
                          emailList: [],
                          reportSettings: {},
                        };
                        newReportValue.emailList = updatedList;
                        setData((prev) => ({
                          ...prev!,
                          report: newReportValue,
                        }));
                      }}
                      subtitle="Add emails you would like reports sent to"
                    />
                    <ReportSettingsField
                      enableSelectAll={true}
                      reportSettings={data.report?.reportSettings}
                      season={season}
                      setReportSettings={(
                        updatedList: Record<string, boolean>,
                      ) => {
                        if (fieldsToEdit.report === true) {
                          const newReportValue = data.report ?? {
                            emailList: [],
                            reportSettings: {},
                          };
                          newReportValue.reportSettings = updatedList;
                          setData((prev) => ({
                            ...prev!,
                            report: newReportValue,
                          }));
                        }
                      }}
                      subtitle="Select what information you want included in your report emails"
                    />
                  </div>
                </section>
                <section className={sectionClass}>
                  <CheckBox
                    label="Edit Images Required"
                    onChange={(checked) =>
                      setFieldsToEdit((prev) => ({
                        ...prev,
                        beforeImagesEnabled: checked,
                        beforeImagesRequiredCount: checked,
                        requiredImageCount: checked,
                      }))
                    }
                  />
                  <div
                    style={{
                      paddingLeft: 30,
                      opacity:
                        fieldsToEdit.beforeImagesEnabled === true ? 1 : 0.3,
                    }}
                  >
                    <RequiredImageSelector
                      data={data}
                      setSeasonField={(field, value) => {
                        setData((prev) => ({
                          ...prev!,
                          [field]: value,
                        }));
                      }}
                    />
                  </div>
                </section>

                <section className={sectionClass}>
                  <CheckBox
                    label="Edit Notes Required"
                    onChange={(checked) =>
                      setFieldsToEdit((prev) => ({
                        ...prev,
                        requireNotes: checked,
                      }))
                    }
                  />
                  <div
                    style={{
                      paddingLeft: 30,
                      opacity: fieldsToEdit.requireNotes === true ? 1 : 0.3,
                    }}
                  >
                    <CheckBox
                      label={`Require operators to enter notes when working on these properties`}
                      checked={data.requireNotes}
                      onChange={(checked) =>
                        setData((prev) => ({
                          ...prev!,
                          requireNotes: checked,
                        }))
                      }
                      disabled={fieldsToEdit.requireNotes !== true}
                    />
                  </div>
                </section>
              </>
            )}
          </form>
        )}
      </ContentWrapper>
    </div>
  );
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const mapStateToProps = (state: any) => {
  const { currentUser } = state;
  return { currentUser };
};

export default connect(mapStateToProps)(BulkEditModal);
